/**
 * Matriz de permissões da tela unificada de Tickets (/tickets).
 *
 * Cada visão corresponde a um universo de tickets do backend (getticketsquadro: dataSource/role)
 * e é liberada por uma ou mais permissões de tela (nome_tela). As ações sobre os tickets da visão
 * (concluir, checklist...) também derivam dessas permissões. Criar ticket, imprimir e editar o
 * agendamento são do usuário, não da visão: valem em qualquer visão que ele abrir.
 * Imprimir: quem tem TicketsCliente (e o superadmin), em todos os tickets das visões.
 *
 * Permissões de tela (nome_tela -> cod_recurso no backend):
 *   TicketsColaborador -> TICKETSCOLABORADOR  tickets atribuídos a mim
 *   TicketsTeam        -> TICKETSTEAM         tickets das minhas equipes (somente acompanhamento)
 *   TicketsDispatcher  -> TICKETSDISPATCHER   tickets das minhas equipes (+ checklist, finalizar e agendamento)
 *   TicketsSupervisor  -> TICKETSSUPERVISOR   todos os tickets (+ checklist e finalizar)
 *   TicketsCriados     -> TICKETSCRIADOS      tickets que abri; cria ticket escolhendo equipe/usuário
 *   TicketsCliente     -> TICKETSCLIENTE      tickets que abri; cria ticket simplificado, imprime e cancela
 *   Tickets            -> TICKET              legado; o backend o aceita como dispatcher (só acompanhamento)
 * Superadmin libera todas as visões e funcionalidades.
 *
 * Coluna Pendente: quem edita cards (Dispatcher, Supervisor); os demais só veem os tickets Pendentes
 * que abriram, sem editar nem mover. Histórico do card: só Supervisor,
 * que é o administrador de tickets. A API aplica as mesmas regras.
 *
 * Data/hora de agendamento: não existe na criação; no detalhe só despachante e superadmin editam
 * (a API aplica a mesma regra em updateticket).
 */

import { hasScreenPermission } from './postLoginNavigation';
import { isSuperadminUser } from './routingModePerfilFilter';

export const TICKET_SCREENS = Object.freeze({
    COLABORADOR: 'TicketsColaborador',
    TEAM: 'TicketsTeam',
    DISPATCHER: 'TicketsDispatcher',
    SUPERVISOR: 'TicketsSupervisor',
    CRIADOS: 'TicketsCriados',
    CLIENTE: 'TicketsCliente',
    LEGADO: 'Tickets',
});

/** Telas que dão acesso à rota /tickets (meta.permissionAnyOf e item de menu). */
export const TICKET_SCREEN_NAMES = Object.freeze(Object.values(TICKET_SCREENS));

/** Ordem de exibição e de escolha da visão padrão. */
export const TICKET_VIEW_KEYS = Object.freeze(['atribuidos', 'equipe', 'todos', 'criados']);

export const TICKET_VIEW_ICONS = Object.freeze({
    atribuidos: 'mdi-account-check',
    equipe: 'mdi-account-group',
    todos: 'mdi-eye',
    criados: 'mdi-ticket-outline',
});

function buildViews(has) {
    const finalizaEquipe = has(TICKET_SCREENS.DISPATCHER);
    const views = {};

    if (has(TICKET_SCREENS.COLABORADOR)) {
        views.atribuidos = {
            dataSource: 'colaborador',
            role: '',
            enableBoardStatusDrag: true,
            editChecklist: true,
            // Quem também é despachante pode preencher checklist sem estar atribuído.
            checklistRequiresAssignment: !finalizaEquipe,
            showCloseTicketButton: false,
            closeWithInternalObservation: false,
            hideInternalObservation: false,
            hidePrecandidatosWriteActions: false,
            prefilterUserTeams: true,
        };
    }

    if (has(TICKET_SCREENS.DISPATCHER) || has(TICKET_SCREENS.TEAM) || has(TICKET_SCREENS.LEGADO)) {
        views.equipe = {
            dataSource: 'dispatcher',
            role: 'dispatcher',
            enableBoardStatusDrag: true,
            editChecklist: finalizaEquipe,
            checklistRequiresAssignment: false,
            showCloseTicketButton: finalizaEquipe,
            closeWithInternalObservation: true,
            hideInternalObservation: false,
            hidePrecandidatosWriteActions: false,
            prefilterUserTeams: true,
        };
    }

    if (has(TICKET_SCREENS.SUPERVISOR)) {
        views.todos = {
            dataSource: 'dispatcher',
            role: 'supervisor',
            enableBoardStatusDrag: true,
            editChecklist: true,
            checklistRequiresAssignment: false,
            showCloseTicketButton: true,
            closeWithInternalObservation: true,
            hideInternalObservation: false,
            hidePrecandidatosWriteActions: false,
            // Supervisão enxerga todos os tickets: não abre filtrada pelas equipes do usuário.
            prefilterUserTeams: false,
        };
    }

    const criados = has(TICKET_SCREENS.CRIADOS);
    const cliente = has(TICKET_SCREENS.CLIENTE);
    if (criados || cliente) {
        views.criados = {
            // 'criados' e 'cliente' retornam o mesmo universo; muda a configuração de campos (tela).
            dataSource: criados ? 'criados' : 'cliente',
            role: '',
            enableBoardStatusDrag: false,
            editChecklist: false,
            checklistRequiresAssignment: false,
            showCloseTicketButton: cliente,
            closeWithInternalObservation: false,
            hideInternalObservation: true,
            hidePrecandidatosWriteActions: true,
            prefilterUserTeams: false,
        };
    }

    return views;
}

/** Formulário de criação do usuário; null quando ele não cria tickets. */
function buildCriacao(has) {
    const criados = has(TICKET_SCREENS.CRIADOS);
    if (!criados && !has(TICKET_SCREENS.CLIENTE)) return null;
    return {
        // Chave de ticket_campos_criacao. Equipe e usuário atribuído ficam na edição do card.
        screen: criados ? 'criados' : 'cliente',
    };
}

/**
 * @param {object} user usuário da sessão
 * @param {Array<{nome_tela: string}>} permissoes telas permitidas
 * @returns {{ visoes: string[], porVisao: Object<string, object>, criacao: object|null, podeImprimir: boolean, podeEditarAgendamento: boolean }}
 */
export function getTicketCapabilities(user, permissoes) {
    const superadmin = isSuperadminUser(user);
    const has = (screen) => superadmin || hasScreenPermission(permissoes, screen);
    const porVisao = buildViews(has);
    return {
        visoes: TICKET_VIEW_KEYS.filter((key) => porVisao[key]),
        porVisao,
        criacao: buildCriacao(has),
        podeImprimir: has(TICKET_SCREENS.CLIENTE),
        podeEditarAgendamento: has(TICKET_SCREENS.DISPATCHER),
        podeVerPendentes: has(TICKET_SCREENS.DISPATCHER) || has(TICKET_SCREENS.SUPERVISOR),
        podeVerHistorico: has(TICKET_SCREENS.SUPERVISOR),
    };
}

export function canAccessTickets(user, permissoes) {
    return getTicketCapabilities(user, permissoes).visoes.length > 0;
}
